import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, User, ShoppingCart, Menu } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import SignInModal from "@/components/SignInModal";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import SearchBox from "@/components/SearchBox";
import NavPillWithFlyout from "@/components/layout/NavPillWithFlyout";
import AccountMenu from "@/components/layout/AccountMenu";
import { useUiChrome } from "@/context/UiChromeContext";

// Top-nav is deliberately trimmed to five high-intent shop entrypoints.
// On desktop each pill opens a category flyout on hover (2-3 top products
// + view-all link). On mobile the pill navigates directly on tap, no
// hover-then-tap dance.
const TOP_NAV = [
  { label: "Pen Kits", to: "/pen-kits", category: "pen" },
  { label: "Raw Vials", to: "/raw-vials", category: "raw" },
  { label: "Bundles", to: "/bundles", category: "bundles" },
  { label: "Accessories", to: "/accessories", category: "accessory" },
  { label: "Best Sellers", to: "/peptides?best=1", category: "best" },
];

export default function Navbar() {
  const { count } = useCart();
  const { user } = useAuth();
  const [signInOpen, setSignInOpen] = useState(false);
  const { openDrawer } = useUiChrome();

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-line bg-white/90 backdrop-blur-xl"
      data-testid="navbar"
    >
      <div className="container-pd">
        {/* Main row — height trimmed from h-16 to h-14 on mobile so the
            combined header (main row + secondary nav) fits in ~90px
            rather than ~105px. Desktop stays h-16 so the search bar
            keeps its click area. */}
        <div className="flex h-14 items-center gap-3 sm:h-16 sm:gap-4">
          <button
            type="button"
            onClick={openDrawer}
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink hover:bg-line/50 -ml-1.5 sm:ml-0"
            aria-label="Open menu"
            data-testid="nav-hamburger"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>

          <div className="flex items-center gap-2 shrink-0">
            <Logo />
            <span className="hidden lg:inline label-mono">· Research compounds</span>
          </div>

          <div className="hidden md:flex flex-1 max-w-xl mx-auto">
            <SearchBox />
          </div>

          <div className="ml-auto flex items-center gap-3 sm:gap-4 shrink-0 text-sm">
            <button className="hidden sm:flex items-center gap-1 text-ink hover:text-brandblue transition-colors" data-testid="lang-selector">
              <span className="text-base leading-none">🇬🇧</span> EN <ChevronDown className="h-3 w-3" />
            </button>
            <CurrencySwitcher className="hidden sm:flex" />
            {user ? (
              <AccountMenu />
            ) : (
              <button
                onClick={() => setSignInOpen(true)}
                className="hidden sm:flex items-center gap-1 text-ink hover:text-brandblue transition-colors"
                data-testid="nav-signin"
              >
                <User className="h-4 w-4" strokeWidth={1.5} /> Sign in
              </button>
            )}
            <Link
              to="/cart"
              className="relative flex h-11 w-11 items-center justify-center -mr-2 sm:mr-0 sm:h-auto sm:w-auto"
              aria-label="View cart"
              data-testid="nav-cart"
            >
              <ShoppingCart className="h-5 w-5 text-ink" strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brandblue px-1 text-[10px] font-bold text-white sm:-right-2 sm:-top-2" data-testid="cart-count">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Trimmed 5-item secondary nav. Padding increased on mobile so
            each pill hits ~44px touch height without disrupting desktop.
            The full navigation is also available in the hamburger drawer. */}
        <nav
          className="flex items-center justify-center gap-3 border-t border-line py-2 text-[12px] font-semibold sm:gap-8 sm:py-2.5 sm:text-[13px]"
          data-testid="secondary-nav"
        >
          {TOP_NAV.map((item) => (
            <NavPillWithFlyout
              key={item.label}
              label={item.label}
              to={item.to}
              category={item.category}
            />
          ))}
        </nav>
      </div>

      <SignInModal open={signInOpen} onOpenChange={setSignInOpen} />
    </header>
  );
}
